import type { ChangeEvent } from 'react';

import { RadioGroupSizeContext } from 'ui/radio-group/radio-group.context';
import {
  RadioGroupHelperText,
  RadioGroupLabel,
  RadioGroupOptions,
  RadioGroupRoot,
} from 'ui/radio-group/radio-group.styles';
import type { RadioGroupProps } from 'ui/radio-group/radio-group.types';
import { useRadioGroup } from 'ui/radio-group/use-radio-group';

const RadioGroup = <TValue extends string = string>({
  children,
  label,
  helperText,
  size = 'medium',
  row = false,
  disabled,
  required,
  error,
  name,
  value,
  defaultValue,
  onChange,
  onBlur,
  ref,
  sx,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}: RadioGroupProps<TValue>) => {
  const { optionsRef, labelId, helperTextId } = useRadioGroup({
    ref,
    hasLabel: label !== undefined,
    hasHelperText: !!helperText,
  });
  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    nextValue: string
  ) => {
    onChange?.(event, nextValue as TValue);
  };

  return (
    <RadioGroupRoot
      groupSize={size}
      disabled={disabled}
      required={required}
      error={error}
      sx={sx}
      data-testid={dataTestId}
    >
      {label !== undefined && (
        <RadioGroupLabel id={labelId} groupSize={size}>
          {label}
        </RadioGroupLabel>
      )}
      <RadioGroupSizeContext value={size}>
        <RadioGroupOptions
          ref={optionsRef}
          groupSize={size}
          row={row}
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onBlur={onBlur}
          aria-label={ariaLabel}
          aria-labelledby={labelId ?? ariaLabelledBy}
          aria-describedby={helperTextId}
          aria-required={required || undefined}
          aria-invalid={error || undefined}
        >
          {children}
        </RadioGroupOptions>
      </RadioGroupSizeContext>
      {helperTextId && (
        <RadioGroupHelperText id={helperTextId}>
          {helperText}
        </RadioGroupHelperText>
      )}
    </RadioGroupRoot>
  );
};

export default RadioGroup;
